/**
 * Quality page. Bundled via import from app.js.
 * Props: api (API.json), toast, user, timezone, onOpenCall(call), onOpenLead(leadId).
 */
const { useState, useEffect, useMemo } = React;

const FLAG_LABELS = {
  very_short: 'Very short call',
  caller_asked_for_human: 'Caller asked for a person',
  handoff_failed: 'Handoff did not connect',
  negative_or_not_interested: 'Not interested',
  no_next_step: 'No next step',
  callback_requested: 'Callback requested',
  long_silence_or_dropped: 'Long silence or dropped',
  voicemail: 'Voicemail'
};
const FLAG_TONE = {
  very_short: 'warn',
  caller_asked_for_human: 'bad',
  handoff_failed: 'bad',
  negative_or_not_interested: 'warn',
  no_next_step: 'warn',
  callback_requested: 'info',
  long_silence_or_dropped: 'bad',
  voicemail: ''
};
const INTEREST = { hot: 'Hot', warm: 'Warm', cold: 'Cold', not_interested: 'Not interested' };
const TABS = [
  ['needs', 'Needs review'],
  ['reviewed', 'Reviewed'],
  ['all', 'All calls']
];

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function ymdInZone(date, timeZone) {
  const map = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(date).forEach((part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
  });
  return map.year + '-' + map.month + '-' + map.day;
}

function addYmd(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function zoneAbbrev(iso, timeZone) {
  const date = iso ? new Date(iso) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(date)
    .find((item) => item.type === 'timeZoneName');
  return part ? part.value : '';
}

function formatWhen(iso, timeZone) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const main = new Intl.DateTimeFormat('en-US', {
    timeZone, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  }).format(date);
  const abbr = zoneAbbrev(iso, timeZone);
  return abbr ? main + ' ' + abbr : main;
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes <= 0) return total + 's';
  return minutes + 'm ' + String(rest).padStart(2, '0') + 's';
}

function formatPhone(value) {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value || '');
  return match ? '(' + match[1] + ') ' + match[2] + '-' + match[3] : (value || '');
}

function weightedScore(criteria, scores) {
  let weighted = 0;
  let weightSum = 0;
  (criteria || []).forEach((criterion) => {
    const raw = scores ? scores[criterion.key] : null;
    if (raw == null || raw === '') return;
    const score = Number(raw);
    const weight = Number(criterion.weight);
    if (!Number.isFinite(score) || !Number.isFinite(weight) || weight <= 0) return;
    weighted += (Math.min(5, Math.max(0, score)) / 5) * weight;
    weightSum += weight;
  });
  if (weightSum <= 0) return null;
  return Math.round((weighted / weightSum) * 100);
}

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = (typeof window !== 'undefined' && window.API_BASE)
    ? String(window.API_BASE).replace(/\/$/, '')
    : (typeof window !== 'undefined' ? window.location.origin : '');
  return base + (String(pathOrUrl).charAt(0) === '/' ? pathOrUrl : '/' + pathOrUrl);
}

function speaker(role) {
  const name = String(role || '').toLowerCase();
  if (name === 'assistant' || name === 'ai' || name === 'agent') return 'ai';
  if (name === 'user' || name === 'caller' || name === 'customer' || name === 'human') return 'caller';
  return 'other';
}

function speakerLabel(kind) {
  if (kind === 'ai') return 'AI';
  if (kind === 'caller') return 'Caller';
  return 'System';
}

function flagLabel(key) {
  return FLAG_LABELS[key] || key;
}

function slugKey(label, used) {
  let base = String(label || 'point').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32);
  if (!base || !/^[a-z]/.test(base)) base = ('point_' + base).replace(/^_+|_+$/g, '').slice(0, 32);
  if (!/^[a-z]/.test(base)) base = 'point';
  let key = base;
  let n = 2;
  while (used.has(key)) {
    key = (base + '_' + n).slice(0, 40);
    n += 1;
  }
  used.add(key);
  return key;
}

function Sparkline({ trend }) {
  const scored = (trend || []).filter((point) => point.avg_score != null && !Number.isNaN(Number(point.avg_score)));
  if (!scored.length) return <p className="qa-spark-empty">Scores show up here after you grade a few calls.</p>;
  const width = 160;
  const height = 48;
  const pad = 4;
  const vals = scored.map((point) => Number(point.avg_score));
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 100);
  const span = max - min || 1;
  const coords = vals.map((value, index) => {
    const x = pad + (index * (width - pad * 2)) / Math.max(vals.length - 1, 1);
    const y = pad + (1 - (value - min) / span) * (height - pad * 2);
    return x + ',' + y;
  }).join(' ');
  const last = coords.split(' ').pop().split(',');
  return (
    <svg className="qa-spark" viewBox={'0 0 ' + width + ' ' + height} role="img" aria-label="Average score by day">
      <polyline fill="none" stroke="var(--primary)" strokeWidth="2" points={coords} />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill="var(--primary)" />
    </svg>
  );
}

function FlagBadges({ flags }) {
  if (!flags || !flags.length) return null;
  return (
    <>
      {flags.map((key) => (
        <span key={key} className={'qa-badge' + (FLAG_TONE[key] ? ' qa-badge--' + FLAG_TONE[key] : '')}>{flagLabel(key)}</span>
      ))}
    </>
  );
}

function ReviewDrawer({ api, toast, callId, timeZone, onClose, onSaved, onOpenCall, onOpenLead }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  function applyDetail(next) {
    const criteria = (next.scorecard && next.scorecard.criteria) || [];
    const saved = (next.review && next.review.scores) || {};
    const initial = {};
    criteria.forEach((criterion) => {
      const value = saved[criterion.key];
      initial[criterion.key] = value == null || value === '' ? null : Number(value);
    });
    setDetail(next);
    setScores(initial);
    setNotes((next.review && next.review.notes) || '');
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api('GET', '/api/quality/calls/' + encodeURIComponent(callId))
      .then((result) => { if (!cancelled) applyDetail(result || {}); })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not open this call.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [callId]);

  useEffect(() => {
    const el = document.getElementById('qa-drawer');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    function onKey(event) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [callId]);

  const criteria = (detail && detail.scorecard && detail.scorecard.criteria) || [];
  const score = useMemo(() => weightedScore(criteria, scores), [criteria, scores]);
  const call = detail && detail.call;
  const review = detail && detail.review;

  async function save(status) {
    setBusy(true);
    try {
      await api('POST', '/api/quality/review', {
        call_id: callId,
        scores,
        notes,
        status
      });
      toast && toast(status === 'needs_coaching' ? 'Marked for coaching' : status === 'flagged' ? 'Flagged for a manager' : 'Review saved');
      onSaved && onSaved();
      const next = await api('GET', '/api/quality/calls/' + encodeURIComponent(callId));
      applyDetail(next || {});
    } catch (err) {
      toast && toast(errorMessage(err, 'Could not save the review.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function scoreWithAi() {
    setBusy(true);
    setAiError('');
    try {
      await api('POST', '/api/quality/calls/' + encodeURIComponent(callId) + '/ai-score', {});
      const next = await api('GET', '/api/quality/calls/' + encodeURIComponent(callId));
      applyDetail(next || {});
      toast && toast('AI score is ready');
    } catch (err) {
      setAiError(errorMessage(err, 'AI scoring failed.'));
    } finally {
      setBusy(false);
    }
  }

  function useAiScores() {
    const suggested = review && review.ai_scores;
    if (!suggested) return;
    const next = { ...scores };
    criteria.forEach((criterion) => {
      const value = suggested[criterion.key];
      next[criterion.key] = value == null || value === '' ? null : Number(value);
    });
    setScores(next);
  }

  return (
    <aside className="qa-drawer" id="qa-drawer" aria-label="Call review">
      <div className="qa-drawer-head">
        <div>
          <h2 className="qa-h2">{(call && call.lead_name) || 'Call review'}</h2>
          {call && call.lead_phone ? <a className="qa-phone" href={'tel:' + call.lead_phone}>{formatPhone(call.lead_phone)}</a> : null}
        </div>
        <button type="button" className="qa-btn" onClick={onClose}>Close</button>
      </div>
      {loading ? <p className="qa-muted">Loading the call…</p> : null}
      {error ? <div className="qa-error" role="alert">{error}</div> : null}
      {call ? (
        <>
          <p className="qa-meta">
            {formatWhen(call.created_at, timeZone)}
            {' · '}{formatDuration(call.duration_seconds)}
            {call.interest_level ? ' · ' + (INTEREST[call.interest_level] || call.interest_level) : ''}
            {call.call_status && call.call_status !== 'completed' ? ' · ' + call.call_status : ''}
          </p>
          <div className="qa-badges"><FlagBadges flags={detail.flags} /></div>
          <div className="qa-actions">
            {call.lead_id && onOpenLead ? <button type="button" className="qa-btn" onClick={() => onOpenLead(call.lead_id)}>Open lead</button> : null}
            {onOpenCall ? <button type="button" className="qa-btn" onClick={() => onOpenCall(call)}>Open in calls</button> : null}
          </div>
          {call.recording_url ? (
            <audio className="qa-audio" controls preload="none" src={absoluteUrl(call.recording_url)}>
              Audio playback is not available in this browser.
            </audio>
          ) : <p className="qa-muted">No recording for this call.</p>}
          <div>
            <h3 className="qa-h3">Transcript</h3>
            {detail.transcript && detail.transcript.length ? (
              <div className="qa-transcript">
                {detail.transcript.map((line) => {
                  const kind = speaker(line.role);
                  return (
                    <div key={line.id || line.created_at + (line.content || '')} className={'qa-line qa-line--' + kind}>
                      <div className="qa-who">{speakerLabel(kind)}</div>
                      <p>{line.content || ''}</p>
                    </div>
                  );
                })}
              </div>
            ) : <p className="qa-muted">No line-by-line transcript is stored for this call.</p>}
          </div>
          <div>
            <h3 className="qa-h3">Scorecard</h3>
            <p className="qa-help">Grade how the AI handled the call. Use N/A when a point did not come up. The score is a weighted average, 0 to 100.</p>
            {criteria.map((criterion) => (
              <div key={criterion.key} className="qa-criterion">
                <div className="qa-criterion-label">{criterion.label}</div>
                {criterion.description ? <p className="qa-help">{criterion.description}</p> : null}
                <div className="qa-score-picks" role="group" aria-label={criterion.label}>
                  {[0, 1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={'qa-score' + (scores[criterion.key] === value ? ' qa-score--on' : '')}
                      aria-pressed={scores[criterion.key] === value}
                      onClick={() => setScores((prev) => ({ ...prev, [criterion.key]: value }))}
                    >{value}</button>
                  ))}
                  <button
                    type="button"
                    className={'qa-score' + (scores[criterion.key] == null ? ' qa-score--on' : '')}
                    aria-pressed={scores[criterion.key] == null}
                    onClick={() => setScores((prev) => ({ ...prev, [criterion.key]: null }))}
                  >N/A</button>
                </div>
              </div>
            ))}
            <p className="qa-total">{score == null ? 'Score —' : 'Score ' + score + ' / 100'}</p>
          </div>
          <div className="qa-ai">
            <h3 className="qa-h3">AI suggestion</h3>
            <p className="qa-help">Scoring uses the transcript and your scorecard. It runs only when you ask, so it does not score every call.</p>
            <div className="qa-actions">
              <button type="button" className="qa-btn qa-btn--primary" disabled={busy} onClick={scoreWithAi}>
                {busy ? 'Working…' : 'Score with AI'}
              </button>
              <button type="button" className="qa-btn" disabled={busy || !(review && review.ai_scores)} onClick={useAiScores}>Use AI scores</button>
            </div>
            {aiError ? <p className="qa-error" role="alert">{aiError}</p> : null}
            {review && review.ai_score != null ? (
              <>
                <p className="qa-total">AI score {review.ai_score} / 100</p>
                {review.ai_scored_at ? <p className="qa-muted">Scored {formatWhen(review.ai_scored_at, timeZone)}</p> : null}
                {review.ai_rationale ? <p>{review.ai_rationale}</p> : null}
                <div className="qa-badges">
                  {criteria.map((criterion) => {
                    const value = review.ai_scores && review.ai_scores[criterion.key];
                    return <span key={criterion.key} className="qa-badge">{criterion.label}: {value == null ? 'N/A' : value}</span>;
                  })}
                </div>
              </>
            ) : <p className="qa-muted">No AI score yet.</p>}
          </div>
          <label className="qa-field">
            Coaching notes
            <textarea className="qa-textarea" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="What should the AI do differently next time?" />
          </label>
          {review && review.reviewed_at ? (
            <p className="qa-muted">
              Last saved {formatWhen(review.reviewed_at, timeZone)}
              {review.reviewer ? ' by ' + review.reviewer : ''}
            </p>
          ) : null}
          <div className="qa-status-row">
            <button type="button" className="qa-btn qa-btn--success" disabled={busy} onClick={() => save('reviewed')}>Mark reviewed</button>
            <button type="button" className="qa-btn qa-btn--warning" disabled={busy} onClick={() => save('needs_coaching')}>Needs coaching</button>
            <button type="button" className="qa-btn qa-btn--danger" disabled={busy} onClick={() => save('flagged')}>Flag</button>
          </div>
          <p className="qa-help">Mark reviewed when the call was fine. Needs coaching when a person should practice this. Flag when a manager should listen soon.</p>
        </>
      ) : null}
    </aside>
  );
}

function ScorecardEditor({ api, toast, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('AI call quality');
  const [criteria, setCriteria] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api('GET', '/api/quality/scorecard')
      .then((result) => {
        if (cancelled) return;
        const card = result && result.scorecard;
        setName((card && card.name) || 'AI call quality');
        setCriteria((card && card.criteria) || []);
      })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not load the scorecard.')); });
    return () => { cancelled = true; };
  }, [open]);

  function update(index, patch) {
    setCriteria((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addPoint() {
    setCriteria((rows) => {
      const used = new Set(rows.map((row) => row.key));
      return rows.concat([{ key: slugKey('New point', used), label: 'New point', weight: 1, description: '' }]);
    });
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('PUT', '/api/quality/scorecard', { name, criteria });
      toast && toast('Scorecard saved');
      onSaved && onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the scorecard.'));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return <button type="button" className="qa-btn" onClick={() => setOpen(true)}>Edit scorecard</button>;
  return (
    <form className="qa-editor" onSubmit={save}>
      <div className="qa-drawer-head">
        <h2 className="qa-h2">Scorecard</h2>
        <button type="button" className="qa-btn" onClick={() => setOpen(false)}>Close</button>
      </div>
      <p className="qa-help">Weights change how much each point affects the 0–100 score. A higher weight matters more.</p>
      {error ? <div className="qa-error" role="alert">{error}</div> : null}
      <label className="qa-field">
        Name
        <input className="qa-input" value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      {criteria.map((criterion, index) => (
        <div key={criterion.key} className="qa-point">
          <label className="qa-field">
            Point
            <input className="qa-input" value={criterion.label} onChange={(event) => update(index, { label: event.target.value })} />
          </label>
          <label className="qa-field">
            Weight
            <input className="qa-input" type="number" min="0.1" step="0.1" value={criterion.weight} onChange={(event) => update(index, { weight: event.target.value })} />
          </label>
          <textarea className="qa-textarea" value={criterion.description || ''} aria-label={'Description for ' + criterion.label} onChange={(event) => update(index, { description: event.target.value })} />
          <button type="button" className="qa-btn" disabled={criteria.length < 2} onClick={() => setCriteria((rows) => rows.filter((_, i) => i !== index))}>Remove</button>
        </div>
      ))}
      <div className="qa-actions">
        <button type="button" className="qa-btn" onClick={addPoint}>Add point</button>
        <button type="submit" className="qa-btn qa-btn--primary" disabled={busy}>{busy ? 'Saving…' : 'Save scorecard'}</button>
      </div>
    </form>
  );
}

export function QualityView({ api, toast, user, timezone, onOpenCall, onOpenLead }) {
  const tz = timezone || 'America/Los_Angeles';
  const canEdit = !!(user && (user.role === 'tenant_admin' || user.role === 'super_admin'));
  const [tab, setTab] = useState('needs');
  const [flag, setFlag] = useState('');
  const [dateFrom, setDateFrom] = useState(() => addYmd(ymdInZone(new Date(), timezone || 'America/Los_Angeles'), -29));
  const [dateTo, setDateTo] = useState(() => ymdInZone(new Date(), timezone || 'America/Los_Angeles'));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [tick, setTick] = useState(0);
  const [batching, setBatching] = useState(false);
  const limit = 50;
  const zone = (summary && summary.timezone) || tz;
  const abbrev = zoneAbbrev(new Date().toISOString(), zone);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => { setOffset(0); }, [tab, flag, dateFrom, dateTo, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (search) params.set('search', search);
    if (flag) params.set('flag', flag);
    if (tab === 'reviewed') params.set('status', 'reviewed,needs_coaching,flagged');
    else if (tab === 'all') params.set('status', 'all');
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const summaryParams = new URLSearchParams();
    if (dateFrom) summaryParams.set('date_from', dateFrom);
    if (dateTo) summaryParams.set('date_to', dateTo);
    Promise.all([
      api('GET', '/api/quality/summary?' + summaryParams.toString()),
      api('GET', '/api/quality/calls?' + params.toString())
    ]).then(([sum, page]) => {
      if (cancelled) return;
      setSummary(sum || null);
      setItems((page && page.items) || []);
      setTotal(page && page.total != null ? Number(page.total) : 0);
    }).catch((err) => {
      if (cancelled) return;
      setError(errorMessage(err, 'Could not load call quality.'));
      setItems([]);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, flag, dateFrom, dateTo, search, offset, tick]);

  const reviewedPct = summary && summary.calls
    ? Math.round((Number(summary.reviewed) / Number(summary.calls)) * 100)
    : null;

  async function scoreListed() {
    const rows = items.slice(0, 10);
    if (!rows.length) return;
    if (typeof window !== 'undefined' && !window.confirm('Score up to ' + rows.length + ' calls on this page with AI? This uses your AI budget.')) return;
    setBatching(true);
    try {
      for (const row of rows) {
        await api('POST', '/api/quality/calls/' + encodeURIComponent(row.id) + '/ai-score', {});
      }
      toast && toast('AI scores are ready');
      setTick((n) => n + 1);
    } catch (err) {
      toast && toast(errorMessage(err, 'AI scoring stopped.'), 'error');
    } finally {
      setBatching(false);
    }
  }

  return (
    <section className="qa">
      <header className="qa-header">
        <div>
          <h1 className="qa-title">Quality</h1>
          <p className="qa-lead">Listen to the calls that need attention and grade how the AI handled them.</p>
          <p className="qa-zone">Times shown in {zone}{abbrev ? ' (' + abbrev + ')' : ''}.</p>
        </div>
        {canEdit ? <ScorecardEditor api={api} toast={toast} onSaved={() => setTick((n) => n + 1)} /> : null}
      </header>
      <div className="qa-tiles">
        <div className="qa-tile">
          <div className="qa-tile-label">Calls</div>
          <div className="qa-tile-value">{summary ? summary.calls : '—'}</div>
          <div className="qa-tile-sub">In this date range</div>
        </div>
        <div className="qa-tile">
          <div className="qa-tile-label">Reviewed</div>
          <div className="qa-tile-value">{reviewedPct == null ? '—' : reviewedPct + '%'}</div>
          <div className="qa-tile-sub">{summary ? summary.reviewed + ' of ' + summary.calls + ' graded' : 'Graded calls'}</div>
        </div>
        <div className="qa-tile">
          <div className="qa-tile-label">Avg score</div>
          <div className="qa-tile-value">{summary && summary.avg_score != null ? summary.avg_score : '—'}</div>
          <div className="qa-tile-sub">{summary && summary.avg_ai_score != null ? 'AI average ' + summary.avg_ai_score : 'Your grades, 0–100'}</div>
        </div>
        <div className="qa-tile">
          <div className="qa-tile-label">Flagged</div>
          <div className="qa-tile-value">{summary ? summary.flagged : '—'}</div>
          <div className="qa-tile-sub">Calls with a reason to listen</div>
        </div>
        <div className="qa-tile qa-tile--trend">
          <div className="qa-tile-label">Score trend</div>
          <Sparkline trend={summary && summary.trend} />
        </div>
      </div>
      {error ? <div className="qa-error" role="alert">{error}</div> : null}
      <div className={'qa-layout' + (selectedId ? ' qa-layout--open' : '')}>
        <div className="qa-main">
          <div className="qa-toolbar">
            <div className="qa-tabs" role="tablist" aria-label="Call queue">
              {TABS.map(([id, label]) => (
                <button key={id} type="button" className={'qa-tab' + (tab === id ? ' qa-tab--on' : '')} aria-pressed={tab === id} onClick={() => setTab(id)}>{label}</button>
              ))}
            </div>
            <button type="button" className="qa-btn" disabled={batching || !items.length} onClick={scoreListed}>
              {batching ? 'Scoring…' : 'Score listed calls with AI'}
            </button>
          </div>
          <div className="qa-filters">
            <label className="qa-field">
              From
              <input className="qa-input" type="date" value={dateFrom} aria-label="From date" onChange={(event) => setDateFrom(event.target.value)} />
            </label>
            <label className="qa-field">
              To
              <input className="qa-input" type="date" value={dateTo} aria-label="To date" onChange={(event) => setDateTo(event.target.value)} />
            </label>
            <button type="button" className="qa-btn" onClick={() => { setDateFrom(''); setDateTo(''); }}>All dates</button>
            <label className="qa-field">
              Search
              <input className="qa-input" value={searchInput} placeholder="Name, phone, or summary" aria-label="Search calls" onChange={(event) => setSearchInput(event.target.value)} />
            </label>
          </div>
          <div className="qa-chips" aria-label="Filter by reason">
            <button type="button" className={'qa-chip' + (!flag ? ' qa-chip--on' : '')} aria-pressed={!flag} onClick={() => setFlag('')}>Any reason</button>
            {Object.keys(FLAG_LABELS).map((key) => {
              const count = summary && summary.by_flag ? summary.by_flag[key] : null;
              return (
                <button key={key} type="button" className={'qa-chip' + (flag === key ? ' qa-chip--on' : '')} aria-pressed={flag === key} onClick={() => setFlag(flag === key ? '' : key)}>
                  {FLAG_LABELS[key]}{count != null ? ' (' + count + ')' : ''}
                </button>
              );
            })}
          </div>
          {loading && !items.length ? <p className="qa-muted">Loading calls…</p> : null}
          {!loading && !items.length ? (
            <div className="qa-empty">
              {tab === 'needs'
                ? 'No calls need review in this range. Flagged calls show up here when the call was very short, the caller asked for a person, or no next step was booked.'
                : 'No calls match these filters.'}
            </div>
          ) : (
            <div className="qa-list">
              {items.map((row) => (
                <div
                  key={row.id}
                  className={'qa-row' + (selectedId === row.id ? ' qa-row--on' : '')}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(row.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedId(row.id);
                    }
                  }}
                >
                  <div>
                    <div className="qa-name">{row.lead_name || 'Unknown caller'}</div>
                    <div className="qa-meta">
                      {row.lead_phone ? formatPhone(row.lead_phone) + ' · ' : ''}
                      {formatWhen(row.created_at, zone)}
                      {' · '}{formatDuration(row.duration_seconds)}
                      {row.interest_level ? ' · ' + (INTEREST[row.interest_level] || row.interest_level) : ''}
                    </div>
                  </div>
                  <div className="qa-badges">
                    <FlagBadges flags={row.flags} />
                    {row.score != null ? <span className="qa-score-pill">{row.score}</span> : null}
                    {row.ai_score != null ? <span className="qa-badge qa-badge--info">AI {row.ai_score}</span> : null}
                    {row.status && row.status !== 'unreviewed' ? (
                      <span className={'qa-badge' + (row.status === 'needs_coaching' ? ' qa-badge--warn' : row.status === 'flagged' ? ' qa-badge--bad' : ' qa-badge--good')}>
                        {row.status === 'needs_coaching' ? 'Needs coaching' : row.status === 'flagged' ? 'Flagged' : 'Reviewed'}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
          {total > limit ? (
            <div className="qa-pager">
              <button type="button" className="qa-btn" disabled={offset <= 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Previous</button>
              <span>{offset + 1}–{Math.min(offset + items.length, total)} of {total}</span>
              <button type="button" className="qa-btn" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Next</button>
            </div>
          ) : null}
        </div>
        {selectedId ? (
          <ReviewDrawer
            api={api}
            toast={toast}
            callId={selectedId}
            timeZone={zone}
            onClose={() => setSelectedId(null)}
            onSaved={() => setTick((n) => n + 1)}
            onOpenCall={onOpenCall}
            onOpenLead={onOpenLead}
          />
        ) : null}
      </div>
    </section>
  );
}
